import React from "react";

/**
 * Lightweight password strength meter matching the backend policy (SEC-005):
 *   - min length 10
 *   - upper, lower, digit, symbol
 *   - not in a short common-password deny-list
 *
 * No external dependency (no zxcvbn). Renders 4 checklist rows + a colored bar.
 */
const COMMON = new Set([
  "password", "password1", "password12", "password123", "password1!",
  "password!", "password1234", "password12!", "password123!", "passw0rd",
  "passw0rd1", "passw0rd!", "p@ssw0rd", "p@ssw0rd!", "p@ssword1",
  "12345678", "123456789", "1234567890", "qwerty123", "qwertyui",
  "qwerty1234", "qwerty12!", "qwerty1!", "letmein1", "letmein!",
  "welcome1", "welcome!", "welcome123", "welcome12!", "welcome1!",
  "admin1234", "administrator", "administrator1", "admin1234!", "admin@123",
  "abcd1234", "abcdefgh", "iloveyou1", "monkey123", "sunshine1",
  "sunshine!", "recrysta1", "recrysta123", "recrysta!", "recrysta1!",
  "hubspot123", "hubspot!", "changeme1", "changeme!", "changeme123",
]);

export function checkPasswordPolicy(pw, email) {
  const rules = {
    length: (pw || "").length >= 10,
    upper: /[A-Z]/.test(pw || ""),
    lower: /[a-z]/.test(pw || ""),
    digit: /[0-9]/.test(pw || ""),
    symbol: /[^A-Za-z0-9]/.test(pw || ""),
    notCommon: !COMMON.has((pw || "").toLowerCase()),
  };
  let notEmailLocal = true;
  if (email && email.includes("@")) {
    const local = email.split("@")[0].toLowerCase();
    if (local.length >= 4 && (pw || "").toLowerCase().includes(local)) notEmailLocal = false;
  }
  rules.notEmail = notEmailLocal;
  const pass = Object.values(rules).every(Boolean);
  return { rules, pass };
}

export function PasswordStrength({ password, email }) {
  const { rules, pass } = checkPasswordPolicy(password, email);
  const score = [rules.length, rules.upper && rules.lower, rules.digit, rules.symbol, rules.notCommon && rules.notEmail].filter(Boolean).length;
  const barColor = score <= 2 ? "bg-red-500" : score === 3 ? "bg-amber-500" : score === 4 ? "bg-emerald-400" : "bg-emerald-500";
  const label = score <= 2 ? "Weak" : score === 3 ? "Fair" : score === 4 ? "Strong" : "Very strong";

  return (
    <div className="mt-2 space-y-2" data-testid="password-strength">
      <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
        <div
          className={`h-full transition-all duration-200 ${barColor}`}
          style={{ width: `${(score / 5) * 100}%` }}
          data-testid="password-strength-bar"
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">
          strength: <span className={pass ? "text-emerald-700" : "text-slate-700"} data-testid="password-strength-label">{label}</span>
        </div>
      </div>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600">
        <Rule ok={rules.length} label="At least 10 characters" />
        <Rule ok={rules.upper} label="One uppercase letter" />
        <Rule ok={rules.lower} label="One lowercase letter" />
        <Rule ok={rules.digit} label="One digit" />
        <Rule ok={rules.symbol} label="One symbol" />
        <Rule ok={rules.notCommon && rules.notEmail} label="Not a common password" />
      </ul>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-300"}`} />
      {label}
    </li>
  );
}

export default PasswordStrength;
